// Sequencing for the Overview attention list (feedback round two §5), kept
// pure so the race it guards against is testable without React.
//
// The problem: every task action PATCHes/DELETEs then triggers a refresh of
// the whole (slow) `/api/admin/overview` payload, so two quick actions leave
// two fetches in flight. Without sequencing the older response lands last
// and resurrects the row Henry just ticked or deleted — into the state AND
// into the localStorage cache, so a hard reload brings it back too.
//
// Every refresh takes a monotonic `seq` (`++listSeq.current`). Two guards
// use it:
//   - `isStale` drops a response that a newer refresh has already overtaken.
//   - `applyRemovals` drops rows the user removed locally from a response
//     that was fetched too early to know about it.
//
// Seq semantics: a removal records `listSeq.current` at the moment it is
// recorded, and `applyRemovals` filters the task out of any response whose
// seq is <= the recorded one (`recorded >= responseSeq`). Only a response
// with a STRICTLY GREATER seq — one started after the server had the change
// — is trusted to speak about that task.
//
// In practice `isStale` already discards every response except the latest
// started one, so `applyRemovals` matters in exactly one window: the delete
// path records the removal (in `.then`) a couple of microtasks before it
// starts the next refresh (in `.finally`). The in-flight response with
// `seq === recorded` is still the latest during that gap, so `>=` is what
// stops it re-showing the row. `>=` also keeps the guard correct whichever
// order a TaskRow calls `onRemoved` and `onChanged` in — the tick path
// records the seq of the refresh `patch()` already started, and filtering
// that (authoritative, row-free) response is harmless. Nothing wrongly
// hides a task reopened elsewhere: every later response has seq > recorded.

/** Task id → the refresh seq that was current when the row was removed. */
export type RemovalMap = Map<string, number>;

/** Remember that `id` was removed locally while refresh `seq` was newest. */
export function recordRemoval(map: RemovalMap, id: string, seq: number): void {
  map.set(id, seq);
}

/** Drop tasks a response is too old to know were removed (see above). */
export function applyRemovals<T extends { id: string }>(
  tasks: T[],
  map: RemovalMap,
  responseSeq: number,
): T[] {
  return tasks.filter((task) => {
    const recorded = map.get(task.id);
    return recorded === undefined || recorded < responseSeq;
  });
}

/** Forget removals a newer response has landed for — the server agrees now. */
export function pruneRemovals(map: RemovalMap, responseSeq: number): void {
  for (const [id, recorded] of map) {
    if (recorded < responseSeq) map.delete(id);
  }
}

/** True when a newer refresh has started since this one — ignore its response. */
export function isStale(seq: number, latest: number): boolean {
  return seq !== latest;
}
